import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Share2 } from "lucide-react";
import { toast } from "sonner";

// Global full-screen image viewer. Open via:
//   window.dispatchEvent(new CustomEvent("reycro:lightbox", { detail: { photos, index } }))
export const Lightbox = () => {
  const [photos, setPhotos] = useState(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const open = (e) => {
      setPhotos(e.detail.photos);
      setIndex(e.detail.index || 0);
    };
    window.addEventListener("reycro:lightbox", open);
    return () => window.removeEventListener("reycro:lightbox", open);
  }, []);

  const close = useCallback(() => setPhotos(null), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + photos.length) % photos.length), [photos]);
  const next = useCallback(() => setIndex((i) => (i + 1) % photos.length), [photos]);
  const share = useCallback(async (e) => {
    e.stopPropagation();
    const url = photos[index];
    try {
      if (navigator.share) {
        await navigator.share({ title: "REYCRO", text: "REYCRO — race milestone", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Image link copied to clipboard");
      }
    } catch (_) {}
  }, [photos, index]);

  useEffect(() => {
    if (!photos) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photos, close, prev, next]);

  return (
    <AnimatePresence>
      {photos && (
        <motion.div
          className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close}
          data-testid="lightbox"
        >
          <button onClick={share} data-testid="lightbox-share" title="Share" className="absolute top-5 right-20 text-white/70 hover:text-[#22AED4] transition-colors z-10">
            <Share2 size={26} />
          </button>
          <button onClick={close} data-testid="lightbox-close" className="absolute top-5 right-5 text-white/70 hover:text-[#BAF173] transition-colors z-10">
            <X size={32} />
          </button>

          {photos.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" className="absolute left-3 sm:left-8 text-white/60 hover:text-white transition-colors z-10">
                <ChevronLeft size={44} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" className="absolute right-3 sm:right-8 text-white/60 hover:text-white transition-colors z-10">
                <ChevronRight size={44} />
              </button>
            </>
          )}

          <motion.img
            key={index}
            src={photos[index]}
            alt="REYCRO event"
            className="max-h-[85vh] max-w-[90vw] object-contain"
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
          />

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-display text-xs uppercase tracking-widest text-white/50">
            {index + 1} / {photos.length}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
